import React, { useState } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  Avatar,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  IconButton,
  Divider,
  Chip,
  Skeleton,
  useTheme,
  useMediaQuery,
  InputBase,
  Paper,
  Badge
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  TrendingUp as OptimizeIcon,
  PostAdd as PostIcon,
  Settings as SettingsIcon,
  Menu as MenuIcon,
  Logout as LogoutIcon,
  AccountCircle as AccountIcon,
  LocationOn as RankingIcon,
  Business as BusinessIcon,
  Insights as InsightsIcon,
  LightbulbOutlined as SuggestionsIcon,
  RateReview as ReviewsIcon,
  Analytics as PerformanceIcon,
  TrendingUp,
  BuildOutlined as ServiceIcon,
  PhotoLibrary as MediaIcon,
  People as UsersIcon,
  EmojiEvents as TrophyIcon,
  Search as SearchIcon,
  Notifications as NotificationsIcon,
  Description as ContentIcon,
  Star as StarIcon,
  BarChart as ChartIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useBusiness } from '../../contexts/BusinessContext';
import { useBusinessSearch } from '../../contexts/BusinessSearchContext';
import BusinessDropdown from '../common/BusinessDropdown';
import amRedLogo from '../../assets/am-red-logo.png';
import { amDesignColors, amDesignTypography } from '../../themes/amDesignSystem';

const drawerWidth = 240;

const Layout = ({ children }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const { user, logout, getRemainingTrialDays, isAdmin, getRoleLabel } = useAuth();
  const { selectedBusiness } = useBusiness();
  const { minimalBusinesses, loading: businessLoading, selectBusiness } = useBusinessSearch();

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Get the locationId from selectedBusiness - prioritize locationId, then googleLocationId, then id
  const getSelectedBusinessLocationId = () => {
    if (!selectedBusiness) return null;
    return selectedBusiness.locationId || selectedBusiness.googleLocationId || selectedBusiness.id;
  };

  // Extract feature and locationId from current URL to ensure highlighting works even during state batching
  const getFeatureAndLocationFromUrl = () => {
    const match = location.pathname.match(/^\/(?:profile|ranking|posts|insights|reviews|report|optimization|medias|activity|business|suggestions|services|agency-overview|all-businesses|all-business-profiles|top-performers|keyword-performance)(?:\/([^/]+))?$/);
    if (!match) return { feature: null, locationId: null };

    const fullPath = match[0];
    const feature = fullPath.split('/')[1];
    const locationId = match[1] || null;

    return { feature, locationId };
  };

  const businessLocationId = getSelectedBusinessLocationId();
  const { feature: urlFeature, locationId: urlLocationId } = getFeatureAndLocationFromUrl();

  // Organized navigation by section
  const navigationSections = [
    {
      section: 'AGENTUR',
      items: [
        { text: 'Übersicht', icon: <InsightsIcon />, path: '/agency-overview' },
       // { text: 'Agentur-Dashboard', icon: <DashboardIcon />, path: '/agency-dashboard' },
        { text: 'Alle Unternehmen', icon: <BusinessIcon />, path: '/all-businesses' },
        { text: 'Alle Geschäftsprofile', icon: <BusinessIcon />, path: '/all-business-profiles' },
        { text: 'Top Performer', icon: <TrophyIcon />, path: '/top-performers' },
        { text: 'Keyword-Performance', icon: <TrendingUp />, path: '/keyword-performance' }
      ]
    },
    {
      section:  'BETRIEB',
      items: [
        { text: 'Profil & Services', icon: <BusinessIcon />, path: businessLocationId ? `/profile/${businessLocationId}` : null },
         { text: 'Inhalte', icon: <ContentIcon />, path: businessLocationId ? `/posts/${businessLocationId}` : null },
        { text: 'Bewertungen', icon: <StarIcon />, path: businessLocationId ? `/reviews/${businessLocationId}` : null },
        { text: 'Leistung', icon: <TrendingUp />, path: businessLocationId ? `/insights/${businessLocationId}` : null },
        { text: 'Sichtbarkeit', icon: <RankingIcon />, path: businessLocationId ? `/ranking/${businessLocationId}` : null },
        { text: 'Bericht', icon: <ChartIcon />, path: businessLocationId ? `/report/${businessLocationId}` : null },
       // { text: 'Ranking Vorschläge', icon: <SuggestionsIcon />, path: businessLocationId ? `/suggestions/${businessLocationId}` : null },
       // { text: 'Library', icon: <MediaIcon />, path: businessLocationId ? `/library/${businessLocationId}` : null },
       // { text: 'Aktivitäten', icon: <TrendingUp />, path: businessLocationId ? `/activity/${businessLocationId}` : null }
      ]
    }
  ];

  // Add admin section for admins
  if (isAdmin()) {
    navigationSections.push({
      section: 'ADMIN',
      items: [
        { text: 'Benutzer', icon: <UsersIcon />, path: '/admin/users' },
        { text: 'Einstellungen', icon: <SettingsIcon />, path: '/settings' }
      ]
    });
  }

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar>
  <Box
    component="img"
    src={amRedLogo}
    alt="A&M Logo"
    sx={{
      height: 30,
      width: 'auto',
      marginRight: 1,
      objectFit: 'contain'
    }}
  />
  <Typography variant="h6" noWrap component="div" color="primary" fontWeight={800} fontFamily={amDesignTypography.fontFamily.heading}>
    LokalMeister
  </Typography>
</Toolbar>

      {/* Trial status */}
      {user?.subscription?.status === 'trial' && (
        <Box sx={{ p: 2 }}>
          <Chip
            label={`${getRemainingTrialDays()} Tage kostenlos`}
            color={getRemainingTrialDays() <= 7 ? 'warning' : 'success'}
            size="small"
            sx={{ width: '100%' }}
          />
        </Box>
      )}

      {/* Main navigation sections - takes up available space */}
      <List sx={{ flex: 1, overflow: 'auto' }}>
        {navigationSections.slice(0, -1).map((section, sectionIndex) => (
          <React.Fragment key={section.section}>
            {/* Section Header */}
            <Typography
              variant="caption"
              sx={{
                px: 2,
                py: 1.5,
                display: 'block',
                fontWeight: 900,
                letterSpacing: 0.5,
                color: amDesignColors.text.secondary,
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                fontFamily: amDesignTypography.fontFamily.heading
              }}
            >
              {section.section}
            </Typography>

            {/* Section Items */}
            {section.items.map((item) => {
              // Skip if no path (business not selected)
              if (!item.path) return null;

              // Check if active: exact match on pathname, or match feature+locationId from URL
              const isActive = location.pathname === item.path || (
                item.path && urlFeature && urlLocationId &&
                item.path === `/${urlFeature}/${urlLocationId}`
              );

              return (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton
                    selected={isActive}
                    onClick={() => {
                      navigate(item.path);
                      if (isMobile) setMobileOpen(false);
                    }}
                    sx={{
                      borderRadius: '20px' ,
                      ...(isActive && {
                        backgroundColor: '#FCE4E7',
                        '& .MuiListItemIcon-root': {
                          color: amDesignColors.primary,
                        },
                        '& .MuiListItemText-primary': {
                          color: amDesignColors.primary,
                          fontWeight: 800,
                        },
                        '&:hover': {
                          backgroundColor: '#FCE4E7',
                        }
                      }),
                      '&:hover:not(.Mui-selected)': {
                        backgroundColor: '#F8F7F9',
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </React.Fragment>
        ))}
      </List>

      {/* Admin section - sticks to bottom */}
      <List sx={{ borderTop: 'none' }}>
        {navigationSections.slice(-1).map((section) => (
          <React.Fragment key={section.section}>
            {/* Section Header */}
            <Typography
              variant="caption"
              sx={{
                px: 2,
                py: 1.5,
                display: 'block',
                fontWeight: 800,
                letterSpacing: 0.5,
                color: amDesignColors.text.secondary,
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                fontFamily: amDesignTypography.fontFamily.heading
              }}
            >
              {section.section}
            </Typography>

            {/* Section Items */}
            {section.items.map((item) => {
              // Skip if no path (business not selected)
              if (!item.path) return null;

              // Check if active: exact match on pathname, or match feature+locationId from URL
              const isActive = location.pathname === item.path || (
                item.path && urlFeature && urlLocationId &&
                item.path === `/${urlFeature}/${urlLocationId}`
              );

              return (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton
                    selected={isActive}
                    onClick={() => {
                      navigate(item.path);
                      if (isMobile) setMobileOpen(false);
                    }}
                    sx={{
                      borderRadius: '20px' ,
                      ...(isActive && {
                        backgroundColor: '#FCE4E7',
                        '& .MuiListItemIcon-root': {
                          color: amDesignColors.primary,
                        },
                        '& .MuiListItemText-primary': {
                          color: amDesignColors.primary,
                          fontWeight: 800,
                        },
                        '&:hover': {
                          backgroundColor: '#FCE4E7',
                        }
                      }),
                      '&:hover:not(.Mui-selected)': {
                        backgroundColor: '#F8F7F9',
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          borderRadius: 0,
          backgroundColor: '#FFFFFF',
          color: '#0A0A0B',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          borderBottom: `1px solid ${amDesignColors.border}`,
          overflow: 'visible',
          height: 64,
          '& .MuiAppBar-root': {
            borderRadius: 0
          }
        }}
      >
        <Toolbar sx={{ height: 64, px: 3, display: 'flex', gap: 2, alignItems: 'center', overflow: 'visible', position: 'relative' }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          {/* Left: Business Selector */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', py: 1, px: 1.5, borderRadius: 1.5, '&:hover': { bgcolor: '#F8F7F9' } }}>
            <Box sx={{ position: 'relative', zIndex: 100 }}>
              {businessLoading ? (
                <Skeleton
                  variant="rounded"
                  width={200}
                  height={40}
                  sx={{ borderRadius: 1 }}
                />
              ) : minimalBusinesses.length > 0 ? (
                <BusinessDropdown
                  businesses={minimalBusinesses}
                  selectedBusiness={selectedBusiness}
                  onBusinessSelect={selectBusiness}
                  loading={businessLoading}
                  placeholder="Unternehmen wählen..."
                  width={240}
                />
              ) : null}
            </Box>
          </Box>

          {/* Right: Notifications + User Profile */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginLeft: 'auto' }}>
           

            {/* Divider */}
            <Divider orientation="vertical" flexItem sx={{ my: 1.5, bgcolor: '#E2E8F0' }} />

            {/* User Profile */}
            <Button
              onClick={handleProfileMenuOpen}
              sx={{
                textTransform: 'none',
                color: '#0A0A0B',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 0.75,
                px: 1,
                '&:hover': {
                  bgcolor: '#F8F7F9',
                }
              }}
            >
              {/* User Info */}
              <Box sx={{ textAlign: 'right' }}>
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    lineHeight: 1.2,
                    fontFamily: 'Plus Jakarta Sans, system-ui'
                  }}
                >
                  {user?.name || 'Benutzer'}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: '#55555D',
                    fontFamily: 'Plus Jakarta Sans, system-ui'
                  }}
                >
                  {getRoleLabel() || 'Administrator'}
                </Typography>
              </Box>

              {/* User Avatar */}
              <Avatar
                src={user?.profilePicture}
                alt={user?.name}
                sx={{
                  width: 36,
                  height: 36,
                  backgroundColor: '#0A0A0B',
                  color: '#FFFFFF',
                  fontSize: '13px',
                  fontWeight: 700,
                  fontFamily: 'Plus Jakarta Sans, system-ui',
                  flexShrink: 0
                }}
              >
                {user?.name?.charAt(0)}
              </Avatar>
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* User Menu */}
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
      >
        {isAdmin() && (
          <MenuItem onClick={() => { navigate('/settings'); handleProfileMenuClose(); }}>
            <ListItemIcon>
              <AccountIcon />
            </ListItemIcon>
            Profil & Einstellungen
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => { logout(); handleProfileMenuClose(); }}>
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          Abmelden
        </MenuItem>
      </Menu>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          bgcolor: amDesignColors.paper
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default Layout;